import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  CartItem,
  SaleItemInterface,
  StockStore,
} from "../../../common/types/interfaces";
import { selectClusterList } from "../../../store/slices/clusterListSlice";

interface StoreIssue {
  itemId: string;
  itemName: string;
  requested: number;
  available: number;
  shortfall: number;
  willTake: number;
  willLeave: number;
}

interface StoreCheckResult {
  hasIssues: boolean;
  issues: StoreIssue[];
  itemsForOrder: CartItem[];
  itemsRemaining: CartItem[];
  totalShortfall: number;
}

export const useCartStoreCheck = (
  cartItems: CartItem[],
  saleItemList: SaleItemInterface[],
  clusterId?: number,
) => {
  const clusters = useSelector(selectClusterList);
  const currentCluster = clusters.find(
    (c) => Number(c.id) === Number(clusterId ?? cartItems[0]?.clusterId),
  );
  const forceStore = currentCluster?.forceStore ?? false;

  const checkStore = useCallback(
    (storeId: number): StoreCheckResult => {
      const issues: StoreIssue[] = [];
      const itemsForOrder: CartItem[] = [];
      const itemsRemaining: CartItem[] = [];

      cartItems.forEach((item) => {
        const saleItem = saleItemList.find(
          (s) => String(s.id) === String(item.saleItemId),
        );

        if (!saleItem) {
          itemsRemaining.push({ ...item });
          return;
        }

        // Без учёта остатков — всегда в заказ
        if (!saleItem.useStock) {
          itemsForOrder.push({ ...item });
          return;
        }

        // Остатки по стору
        const storeStock = (saleItem.stock_stores || []).find(
          (s: StockStore) => s.store?.id === storeId,
        );
        const available = storeStock
          ? storeStock.quantity - storeStock.reserved
          : 0;

        // Стоп-лист для этого стора
        const stop = (saleItem.item_stops || []).find(
          (s) => s.storeId === storeId,
        );
        const isStopped = stop?.isStopped === true;

        if (isStopped || available <= 0) {
          itemsRemaining.push({ ...item });
          issues.push({
            itemId: item.id,
            itemName: saleItem.name,
            requested: item.quantity,
            available: 0,
            shortfall: item.quantity,
            willTake: 0,
            willLeave: item.quantity,
          });
          return;
        }

        if (available >= item.quantity) {
          itemsForOrder.push({ ...item });
        } else {
          itemsForOrder.push({ ...item, quantity: available });
          itemsRemaining.push({
            ...item,
            quantity: item.quantity - available,
          });
          issues.push({
            itemId: item.id,
            itemName: saleItem.name,
            requested: item.quantity,
            available,
            shortfall: item.quantity - available,
            willTake: available,
            willLeave: item.quantity - available,
          });
        }
      });

      const totalShortfall = issues.reduce((sum, i) => sum + i.shortfall, 0);

      return {
        hasIssues: issues.length > 0,
        issues,
        itemsForOrder,
        itemsRemaining,
        totalShortfall,
      };
    },
    [cartItems, saleItemList, forceStore],
  );

  return { checkStore, forceStore };
};
